import { Canvas } from "@react-three/fiber";
import { useCanvasStore } from "@state/canvasStore";
import styled from "styled-components";
import { handleContextLoss } from "./utils/context";
import { useLocation } from "react-router-dom";

import { Vector3 } from "three";
import { useCallback, useEffect, useRef, useState } from "react";

import { useStore } from "@state/store";

import TheLocation from "./TheLocation";
import Residences from "./Residences";
import SFrameJoined from "./optimise/SFrameJoined";
import RenderSceneCamera from "./optimise/RenderSceneCamera";
import { OrbitControls } from "@react-three/drei";
import Popup from "./Popup";

const preloadImages = (imageUrls: string[]) => {
  imageUrls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });
};

export const TCanvas = () => {
  const setCanvasReady = useCanvasStore((s) => s.setCanvasReady);
  const splatDataOnLoad = () => {
    setCanvasReady(true);
  };

  const { pathname } = useLocation();

  const [src, setSrc] = useState(`loc`);

  useEffect(() => {
    const imageUrls = Array.from(
      { length: 100 },
      (_, i) => `frames/${src}/pos_${i}.webp`,
    );
    preloadImages(imageUrls);
  }, [src]);

  useEffect(() => {
    if (pathname === "/residences") {
      setSrc("res");
    } else if (pathname === "/the-location") {
      setSrc("loc");
    }
  }, [pathname]);

  const canvasWrapperRef = useRef<HTMLDivElement>(null);

  const selectedUnit = useStore((s) => s.selectedUnit);
  const filteredUnits = useStore((s) => s.filteredUnits);
  const syncState = useStore((s) => s.syncState);

  const handleClickMesh = (unit: any) => {
    if (selectedUnit?.[0]?.unitName === unit.unitName) {
      syncState({ selectedUnit: [], controlsVisible: true });
    } else {
      syncState({ selectedUnit: [unit], controlsVisible: false });
    }
  };

  const centerRef = useRef(new Vector3(0, 0, 0));

  const [center, setCenter] = useState(centerRef.current);
  const onSelectedUnitChange = useCallback((unit) => {
    setCenter(unit.center);
  }, []);

  ////////////////////////////////

  const [activeId, setActiveId] = useState<number>(36);
  const placeholder = useRef<number>(0);

  const handleSetActive = (id: number) => {
    if (placeholder.current !== id) {
      placeholder.current = id;
      const imageUrl = `frames/${src}/pos_${99 - id}.webp`;

      if (img_0.current) {
        img_0.current.src = imageUrl;
      }
    }
  };

  const img_0 = useRef<HTMLImageElement>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const receiverRef = useRef<HTMLCanvasElement>(null);

  const handleEvent = (event: PointerEvent) => {
    if (canvasRef.current) {
      const clonedEvent = new PointerEvent(event.type, event);
      canvasRef.current.dispatchEvent(clonedEvent);
    }
  };

  const renderCanvasOn = ["/residences", "/the-location"];
  return (
    <CanvasWrapper
      style={{
        display: renderCanvasOn.includes(pathname) ? "block" : "none",
        pointerEvents: renderCanvasOn.includes(pathname) ? "auto" : "none",
        left: pathname === "/residences" ? "25%" : "0%",
        width: pathname === "/residences" ? "75%" : "100%",
      }}
      ref={canvasWrapperRef}
    >
      <Popup active={selectedUnit?.[0]} />

      <img
        onLoad={(e) => {
          const url = (e.target as HTMLImageElement).src;
          const id = url.split("_")[1].split(".")[0];
          setActiveId(parseInt(id));
        }}
        style={{
          // display: "none",
          opacity: 1,
        }}
        src={`frames/${src}/pos_36.webp`}
        ref={img_0}
      />
      <Canvas
        gl={{ preserveDrawingBuffer: true }}
        onPointerOver={() => {
          document.body.style.cursor = "pointer";
        }}
        onPointerOut={() => {
          document.body.style.cursor = "default";
        }}
        onPointerMove={(e) => handleEvent(e.nativeEvent)}
        onPointerDown={(e) => handleEvent(e.nativeEvent)}
        onPointerUp={(e) => handleEvent(e.nativeEvent)}
        ref={receiverRef}
        style={{
          zIndex: 99,
          opacity: 1,
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
        dpr={2}
      >
        <RenderSceneCamera activeId={activeId} />
        <group position={[0.12, 0, -0.6]}>
          <TheLocation renderSplat={false} splatDataOnLoad={splatDataOnLoad} />
          {/* <axesHelper args={[10]} /> */}

          <Residences
            recordMode={false}
            renderSplat={false}
            splatDataOnLoad={splatDataOnLoad}
            center={center}
            pathname={pathname}
            selectedUnit={selectedUnit}
            filteredUnits={filteredUnits}
            onSelectedUnitChange={onSelectedUnitChange}
            handleClickMesh={handleClickMesh}
          />
        </group>
      </Canvas>
      <Canvas
        ref={canvasRef}
        style={{
          position: "absolute",
        }}
        dpr={2}
        onCreated={({ gl }) => {
          handleContextLoss(gl);
        }}
      >
        <TargetCam />
        {
          /* <AnimateCamera
          controlsSettings={controlsSettings}
          initCam={initCam}
          pathname={pathname}
        /> */
        }

        <SFrameJoined handleSetActive={handleSetActive} />
      </Canvas>
    </CanvasWrapper>
  );
};

const TargetCam = () => {
  return (
    <>
      <OrbitControls
        onChange={(e) => {
          console.log(e.target.getPolarAngle());
        }}
        minPolarAngle={1.2}
        maxPolarAngle={1.2}
        enablePan={false}
        enableZoom={false}
        enableRotate={true}
        rotateSpeed={0.27}
      />
    </>
  );
};

const CanvasWrapper = styled.div`
  z-index: 4;
  position: absolute;
  top: 0;

  height: 100%;
  img {
    opacity: 1;
    z-index: 10;
    position: absolute;
    pointer-events: none;
    user-select: none;
    width: 100%;
    height: 100%;

    object-fit: cover;
    object-position: center;
  }
`;

export default TCanvas;
